<template>
  <div>
    <div class="page-head">
      <h1>Registrations</h1>
      <p>Student registrations for your enrolled subjects in the <b>current quarter ({{ quarterLabel }})</b>. Search a student to see their quarter at a glance.</p>
    </div>
    <div v-if="quarterCode" class="head-actions">
      <div class="notice inline">
        <b>{{ quarterLabel }}:</b> {{ totalRegistrations }} registrations across {{ subjectRows.length }} subjects
      </div>
      <button
        v-if="subjectRows.length"
        type="button"
        class="btn btn-outline btn-sm"
        @click="exportCsv"
      >
        Export CSV
      </button>
    </div>

    <div class="searchbar">
      <input
        v-model="searchQuery"
        type="text"
        placeholder="Search a student by name or email…"
        @input="onSearchInput"
      >
      <button v-if="selectedStudent" type="button" class="btn btn-outline btn-sm" @click="clearStudent">Clear</button>
    </div>
    <div v-if="searchResults.length" class="search-results">
      <div
        v-for="s in searchResults"
        :key="s.student_id"
        class="item"
        @click="selectStudent(s)"
      >
        {{ s.name }}
        <span class="em">{{ s.email || '' }}</span>
      </div>
    </div>

    <div v-if="selectedStudent" class="student-card">
      <div class="head">
        <div>
          <h3>{{ selectedStudent.name }}</h3>
          <div class="em">{{ selectedStudent.email || '—' }}</div>
        </div>
        <div class="q">{{ selectedStudentRegs.length }}<small>subject(s) this quarter</small></div>
      </div>
      <dsa-loading v-if="studentLoading" compact text="Loading…" />
      <table v-else>
        <thead><tr><th>Subject</th><th>Exam date</th><th>Status</th><th>Score</th><th>Registered On</th></tr></thead>
        <tbody>
          <tr v-for="r in selectedStudentRegs" :key="r.id">
            <td>
              <span class="subject">
                {{ r.subject_name || r.dsa_subject_name }}
                <small>{{ subjectCategory(r) }}</small>
              </span>
            </td>
            <td>{{ r.exam_date || '—' }}</td>
            <td><span class="chip" :class="statusChip(r)">{{ displayStatus(r) }}</span></td>
            <td>{{ scoreCell(r.score) }}</td>
            <td>{{ formatDate(r.create_time) }}</td>
          </tr>
          <tr v-if="!selectedStudentRegs.length">
            <td colspan="5" style="color:var(--muted)">No current-quarter registrations found</td>
          </tr>
        </tbody>
      </table>
    </div>

    <dsa-loading v-if="loading" text="Loading registrations…" />
    <div v-else-if="!subjectRows.length" class="empty">
      <h3>No enrolled subjects</h3>
      <p>Enroll subjects from the catalog to see student registrations here.</p>
      <router-link to="/institution/catalog" class="btn btn-outline">Subject catalog</router-link>
    </div>
    <div v-else>
      <div
        v-for="(row, i) in subjectRows"
        :key="row.subject_code"
        class="subj-row"
        :class="{ open: openIndex === i || highlightSubject === row.subject_code }"
      >
        <div class="head" @click="toggleRow(i)">
          <div>
            <h3>{{ row.subject_name }}<span class="scat">{{ subjectCategory(row) }}</span></h3>
          </div>
          <div class="meta">
            <span>Registered <span class="cnt">{{ row.registrations.length }}</span></span>
            <span class="chev">&#9656;</span>
          </div>
        </div>
        <div class="reg-list">
          <p v-if="row.registrations.length" class="note">Students registered for this subject · exam date shown.</p>
          <div v-if="!row.registrations.length" class="empty" style="padding:12px 0;border:none">No student registrations yet</div>
          <table v-else>
            <thead><tr><th>Student</th><th>Registered On</th><th>Exam date</th><th>Status</th><th>Score</th><th style="text-align:right">Actions</th></tr></thead>
            <tbody>
              <tr v-for="reg in row.registrations" :key="reg.id">
                <td>
                  <button type="button" class="entry subject-btn" @click.stop="selectStudent({ student_id: reg.student_id, name: reg.studentName, email: reg.email })">
                    <span class="subject">{{ reg.studentName }}<small v-if="reg.email">{{ reg.email }}</small></span>
                  </button>
                </td>
                <td>{{ formatDate(reg.create_time) }}</td>
                <td>{{ reg.exam_date || '—' }}</td>
                <td><span class="chip" :class="statusChip(reg)">{{ displayStatus(reg) }}</span></td>
                <td>{{ scoreCell(reg.score) }}</td>
                <td class="td-actions" style="text-align:right">
                  <button
                    v-if="reg.id && hasReleasedScore(reg.score)"
                    type="button"
                    class="entry"
                    @click.stop="openTranscript(reg.id)"
                  >Score report</button>
                  <router-link
                    :to="`/institution/past-quarters/${row.subject_code}`"
                    class="entry"
                    @click.native.stop
                  >History</router-link>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import {
  getActiveQuarters,
  getInstitutionDashboard,
  getRegistrationsBySubject,
  getRegistrationsByStudent,
  searchInstitutionStudents,
} from '@/api/dsa'
import { getInstitutionAccountId } from '@/utils/store'
import {
  formatDate,
  studentDisplayName,
  unwrapId,
  pickCurrentQuarter,
  formatScoreDisplay,
  subjectCategory,
  applicationStatusLabel,
  isPendingPayment,
} from '@/utils/dsaFormat'

export default {
  name: 'InstitutionRegistrations',
  data() {
    return {
      loading: true,
      studentLoading: false,
      quarterCode: '',
      quarterLabel: '',
      subjectRows: [],
      openIndex: 0,
      highlightSubject: '',
      searchQuery: '',
      searchResults: [],
      searchTimer: null,
      selectedStudent: null,
      selectedStudentRegs: [],
      pendingStudentId: '',
    }
  },
  computed: {
    totalRegistrations() {
      return this.subjectRows.reduce((n, r) => n + r.registrations.length, 0)
    },
  },
  mounted() {
    if (this.$route.query.subject) {
      this.highlightSubject = String(this.$route.query.subject).toUpperCase()
    }
    if (this.$route.query.student_id) {
      this.pendingStudentId = String(this.$route.query.student_id)
    }
    this.load()
  },
  beforeDestroy() {
    clearTimeout(this.searchTimer)
  },
  methods: {
    formatDate,
    subjectCategory,
    scoreCell(score) {
      const view = formatScoreDisplay(score)
      return view ? view.text : '—'
    },
    hasReleasedScore(score) {
      return formatScoreDisplay(score) != null
    },
    openTranscript(application_id) {
      this.$router.push(`/institution/transcript/${application_id}`)
    },
    exportCsv() {
      const rows = [['Subject', 'Student', 'Email', 'Exam Date', 'Status', 'Score', 'Registered On']]
      this.subjectRows.forEach(row => {
        row.registrations.forEach(reg => {
          rows.push([
            row.subject_name,
            reg.studentName,
            reg.email || '',
            reg.exam_date || '',
            this.displayStatus(reg),
            this.scoreExport(reg.score),
            formatDate(reg.create_time),
          ])
        })
      })
      const csv = rows.map(r => r.map(this.escapeCsvCell).join(',')).join('\r\n')
      const blob = new Blob([`\uFEFF${csv}`], { type: 'text/csv;charset=utf-8;' })
      const url = URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      a.download = `dsa-registrations-${this.quarterCode || 'quarter'}.csv`
      a.click()
      URL.revokeObjectURL(url)
    },
    escapeCsvCell(value) {
      const text = value == null || value === '—' ? '' : String(value)
      return `"${text.replace(/"/g, '""')}"`
    },
    scoreExport(score) {
      const view = formatScoreDisplay(score)
      return view ? view.text : ''
    },
    toggleRow(i) {
      this.openIndex = this.openIndex === i ? -1 : i
    },
    clearStudent() {
      this.selectedStudent = null
      this.selectedStudentRegs = []
      this.searchQuery = ''
      this.searchResults = []
    },
    onSearchInput() {
      clearTimeout(this.searchTimer)
      const q = this.searchQuery.trim()
      if (q.length < 2) {
        this.searchResults = []
        return
      }
      this.searchTimer = setTimeout(() => this.runSearch(q), 280)
    },
    async runSearch(q) {
      const account_id = getInstitutionAccountId()
      try {
        const res = await searchInstitutionStudents(account_id, q)
        if (res.data && res.data.code === 0) {
          this.searchResults = res.data.data || []
        }
      } catch (e) {
        this.searchResults = []
      }
    },
    async selectStudent(s) {
      this.selectedStudent = s
      this.searchQuery = s.name || ''
      this.searchResults = []
      this.studentLoading = true
      try {
        const res = await getRegistrationsByStudent(
          getInstitutionAccountId(),
          this.quarterCode,
          s.student_id
        )
        const regs = (res.data && res.data.data) || []
        this.selectedStudentRegs = regs.map(r => ({
          id: unwrapId(r) || r.application_id,
          subject_name: r.dsa_subject_name || r.dsa_subject_code,
          dsa_category: (r.project && r.project.dsa_category) || r.dsa_category || '',
          project_product_line: (r.project && r.project.project_product_line) || r.project_product_line || '',
          project_category: (r.project && r.project.project_category) || r.project_category || '',
          create_time: r.create_time,
          exam_date: r.exam_date || (r.project && r.project.exam_date) || '',
          status: r.application_status,
          application_status: r.application_status,
          status_label: r.status_label || applicationStatusLabel(r.application_status),
          pending_payment: r.application_status === '待缴费' || r.exam_status === 'pending_payment',
          score: r.score,
        }))
      } catch (e) {
        this.selectedStudentRegs = []
      } finally {
        this.studentLoading = false
      }
    },
    displayStatus(r) {
      if (isPendingPayment(r)) return 'Pending'
      return applicationStatusLabel(r.status_label || r.status || r.application_status) || '—'
    },
    statusChip(r) {
      if (isPendingPayment(r)) return 'pend'
      const label = String(this.displayStatus(r)).toLowerCase()
      if (label.includes('paid') || label.includes('redeemed')) return 'open'
      return 'soon'
    },
    mapReg(r) {
      const student = r.student || {}
      return {
        id: unwrapId(r) || r.application_id,
        student_id: r.student_id || unwrapId(student) || '',
        studentName: studentDisplayName(student) || 'Student',
        email: student.student_email,
        create_time: r.create_time,
        status: r.application_status,
        application_status: r.application_status,
        status_label: r.status_label || applicationStatusLabel(r.application_status),
        pending_payment: r.application_status === '待缴费' || r.exam_status === 'pending_payment',
        exam_date: r.exam_date || (r.project && r.project.exam_date) || '',
        score: r.score,
      }
    },
    async load() {
      const account_id = getInstitutionAccountId()
      if (!account_id) return
      try {
        const qRes = await getActiveQuarters()
        const current = pickCurrentQuarter((qRes.data && qRes.data.data) || [])
        this.quarterCode = current ? current.quarter_code : ''
        this.quarterLabel = (current && (current.quarter_name || current.quarter_code)) || this.quarterCode
        if (!this.quarterCode) {
          this.$emit('toast', 'No active quarter configured')
          return
        }
        const dashRes = await getInstitutionDashboard(account_id, this.quarterCode)
        const subjects = (dashRes.data && dashRes.data.data && dashRes.data.data.subjects) || []
        if (dashRes.data && dashRes.data.data && dashRes.data.data.quarter_name) {
          this.quarterLabel = dashRes.data.data.quarter_name
        }
        const rows = await Promise.all(subjects.map(async (sub) => {
          const res = await getRegistrationsBySubject(account_id, this.quarterCode, sub.subject_code)
          const regs = (res.data && res.data.data) || []
          return {
            subject_code: sub.subject_code,
            subject_name: sub.subject_name,
            dsa_category: sub.dsa_category || '',
            project_product_line: sub.project_product_line || '',
            registrations: regs.map(this.mapReg),
          }
        }))
        this.subjectRows = rows
        if (this.highlightSubject) {
          const idx = rows.findIndex(r => r.subject_code === this.highlightSubject)
          if (idx >= 0) this.openIndex = idx
        }
        if (this.pendingStudentId) {
          const hit = rows.flatMap(r => r.registrations).find(r => r.student_id === this.pendingStudentId)
          if (hit) {
            this.selectStudent({ student_id: hit.student_id, name: hit.studentName, email: hit.email })
          }
        }
      } catch (e) {
        this.$emit('toast', 'Failed to load registrations')
      } finally {
        this.loading = false
      }
    },
  },
}
</script>

<style scoped>
.head-actions{display:flex;align-items:center;justify-content:space-between;gap:16px;margin-bottom:16px;flex-wrap:wrap}
.head-actions .notice.inline{margin:0;flex:1}
.searchbar{display:flex;gap:10px;margin-bottom:12px;align-items:center}
.searchbar input{flex:1;max-width:420px;border:1px solid #cbd5e1;border-radius:10px;padding:10px 16px;font-size:14px;font-family:inherit;background:var(--card);color:var(--ink);outline:none}
.searchbar input:focus{border-color:var(--blue)}
.search-results{max-width:420px;margin-bottom:18px}
.search-results .item{background:var(--card);border:1px solid var(--line);border-top:none;padding:10px 16px;font-size:13.5px;cursor:pointer}
.search-results .item:first-child{border-top:1px solid var(--line);border-radius:10px 10px 0 0}
.search-results .item:last-child{border-radius:0 0 10px 10px}
.search-results .item:hover{background:var(--blue-light)}
.search-results .item .em{display:block;font-size:12px;color:var(--muted)}
.student-card{background:var(--card);border:1px solid var(--line);border-radius:var(--radius);padding:20px 24px;margin-bottom:24px}
.student-card .head{display:flex;align-items:center;justify-content:space-between;flex-wrap:wrap;gap:10px}
.student-card .head h3{font-size:17px;font-weight:700}
.student-card .head .em{font-size:12.5px;color:var(--muted);margin-top:2px}
.student-card .head .q{font-size:22px;font-weight:700;color:var(--blue)}
.student-card .head .q small{display:block;font-size:12px;color:var(--muted);font-weight:400}
.student-card table{margin-top:14px}
.subj-row{background:var(--card);border:1px solid var(--line);border-radius:var(--radius);margin-bottom:12px;overflow:hidden}
.subj-row .head{display:flex;align-items:center;justify-content:space-between;gap:14px;padding:16px 22px;cursor:pointer}
.subj-row .head:hover{background:#fafcff}
.subj-row .head h3{font-size:15px;font-weight:600}
.subj-row .head .scat{font-size:12px;color:var(--muted);display:block;margin-top:2px}
.subj-row .head .meta{display:flex;align-items:center;gap:22px;font-size:13px;color:var(--gray)}
.subj-row .head .meta .cnt{font-size:17px;font-weight:700;color:var(--blue);min-width:34px;text-align:right}
.subj-row .head .chev{color:var(--muted);font-size:12px;transition:transform .15s}
.subj-row.open .chev{transform:rotate(90deg)}
.reg-list{display:none;border-top:1px solid var(--line);padding:4px 22px 18px;background:#fafbfd}
.subj-row.open .reg-list{display:block}
.reg-list .note{font-size:12px;color:var(--muted);margin:12px 0 8px}
.subject-btn{background:none;border:none;padding:0;text-align:left;cursor:pointer}
</style>
